import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { useAppAuth } from '../../hooks/useAppAuth';
import { useUserRoles } from '../../hooks/useUserRoles';
import { FeatureFlag, useFeatureFlag } from '../../services/featureFlag.service';
import { shortid } from '../../services/id.service';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import SyncStatusLine from './SyncStatusLine';

/**
 * Greeting + "Ask Ellie" entry point at the top of the home page. Asking opens
 * a fresh conversation with the question already sent.
 *
 * The ask box is Ellie, so it lives and dies with the ai-assistant flag — a
 * tenant without it keeps the greeting and sync line and gets no entry point
 * into a product they don't have. The flag defaults off until LaunchDarkly
 * resolves, so the box appears late rather than flashing for a tenant that
 * shouldn't see it.
 *
 * Still to do: suggested questions. There were three hardcoded ones here, which
 * named sources a tenant may not even have — worse than nothing. They come back
 * when they can be drawn from the tenant's own data.
 */

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function WelcomeHeader() {
  const navigate = useNavigate();
  const { user } = useAppAuth();
  // Build freshness and its logs link are tenant-internal — a ReadOnly_Partner
  // gets the greeting and Ask Ellie only.
  const { canViewDataManagement } = useUserRoles();
  const isAiAssistantEnabled = useFeatureFlag(FeatureFlag.AI_ASSISTANT, false);
  const [question, setQuestion] = useState('');

  const firstName = (user?.name || '').split(' ')[0];

  function ask(prompt: string) {
    const question = prompt.trim();
    if (!question) return;
    // Start a fresh thread with its own URL, handing the question over on
    // history state — it's the opening message, not part of the address, and a
    // refresh shouldn't re-ask it.
    navigate(`/ellie/conversation/${shortid()}`, { state: { initialQuestion: question } });
  }

  return (
    <div className="px-8 py-8 border-b border-border bg-gradient-to-br from-hero-start via-hero-mid to-hero-end text-foreground">
      <h1 className="text-3xl font-bold">
        {greeting()}
        {firstName ? `, ${firstName}` : ''}
      </h1>

      {canViewDataManagement && <SyncStatusLine />}

      {isAiAssistantEnabled && (
        <div className="mt-5 flex items-center gap-2 rounded-lg border border-border bg-card/70 p-2 pl-4 max-w-4xl">
          <MessageCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') ask(question);
            }}
            placeholder="Ask Ellie"
            className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:ring-0 shadow-none"
          />
          <Button onClick={() => ask(question)} className="flex-shrink-0">
            Ask
          </Button>
        </div>
      )}
    </div>
  );
}
